import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Spinner,
  Text,
  mergeClasses,
} from "@fluentui/react-components";
import {
  useSharedDialogFooterStyles,
  useSharedTagStyles,
  useSharedDialogContentStyles,
} from "theme/sharedStyles";
import { updateWorkspace } from "api";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { IUser } from "models/IUser";
import { IUserDetails } from "models/IUserDetails";
import { IWorkspace } from "models/IWorkspace";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { IAzureUser } from "models/IAzureUser";
import { mapAzureUsersToModel } from "utils/generalUtils";
import { updateWorkspaceInSlice } from "../../../redux/workspacesSlice";
import {
  deduplicateOptions,
  optionToUserDetails,
  userDetailsToOption,
} from "../editDialog/EditWorkspaceDialog";
import { useAddMemberDialogStyles } from "./AddMemberDialog.styles";
import {
  DIALOG_LABELS,
  ERROR_MESSAGES,
  IAddMemberDialogProps,
  PICKER_IDS,
} from "./AddMemberDialog.types";

export const AddMemberDialog = (props: IAddMemberDialogProps): JSX.Element => {
  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );

  const dispatch = useDispatch();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();
  const styles = useAddMemberDialogStyles();
  const sharedTagStyles = useSharedTagStyles();

  const [teamMembers, setTeamMembers] = useState<CustomOption[]>([]);
  const [newMembers, setNewMembers] = useState<CustomOption[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const originalMembers = useRef<CustomOption[]>([]);

  const isAddOnly = props.canRemoveExisting === false;

  useEffect((): void => {
    const existingMembers = (props.workspace.teamMembers || []).map(
      userDetailsToOption,
    );
    if (isAddOnly) {
      originalMembers.current = existingMembers;
      setNewMembers([]);
    } else {
      setTeamMembers(existingMembers);
    }
  }, [props.workspace, isAddOnly]);

  const handleSelectedTeamMembers = (selected: CustomOption[]): void => {
    setTeamMembers(deduplicateOptions(selected));
  };

  const handleNewMembers = (selected: CustomOption[]): void => {
    const filtered = selected.filter(
      (s) => !originalMembers.current.some((o) => o.key === s.key),
    );
    setNewMembers(deduplicateOptions(filtered));
  };

  const readResourcesForPickerFieldFiltered = async (
    searchedValue: string,
  ): Promise<IAzureUser[]> => {
    const mappedUsers = mapAzureUsersToModel(users);
    return mappedUsers.filter((resource: IAzureUser) =>
      resource.Name?.toLowerCase().includes(searchedValue.toLowerCase()),
    );
  };

  const handleSubmit = async (): Promise<void> => {
    setIsSaving(true);
    try {
      const ownerOptions = (props.workspace.owners || []).map(
        userDetailsToOption,
      );
      const allTeamMembers = isAddOnly
        ? deduplicateOptions([...originalMembers.current, ...newMembers])
        : teamMembers;
      const allMembers = deduplicateOptions([
        ...allTeamMembers,
        ...ownerOptions,
      ]);

      const members: IUserDetails[] = allMembers
        .filter((member) => member.id)
        .map(optionToUserDetails);

      const workspacePayload = {
        ...props.workspace,
        teamMembers: members,
        owners: props.workspace.owners,
      };

      await updateWorkspace({
        id: props.workspace.id,
        workspace: workspacePayload,
      });

      const resultWorkspace: IWorkspace = {
        ...props.workspace,
        teamMembers: members,
        updatedAt: new Date().toISOString(),
      };

      dispatch(updateWorkspaceInSlice(resultWorkspace));
      props.onDismiss(true, resultWorkspace);
    } catch (e) {
      console.error("Error adding members:", e);
      setErrorMessage(ERROR_MESSAGES.SAVE_ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={props.isOpen} modalType="alert">
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{DIALOG_LABELS.TITLE}</DialogTitle>
          <DialogContent className={contentStyles.formContainer}>
            {isSaving && (
              <div className={contentStyles.formLoadingOverlay}>
                <Spinner label={`${DIALOG_LABELS.SAVING}...`} />
              </div>
            )}
            {isAddOnly && originalMembers.current.length > 0 && (
              <Field label={DIALOG_LABELS.EXISTING_MEMBERS}>
                <div className={styles.chipList}>
                  {originalMembers.current.map((member) => (
                    <span
                      key={member.key}
                      className={mergeClasses(
                        sharedTagStyles.tag,
                        sharedTagStyles.tagAccent,
                      )}
                    >
                      <Avatar
                        name={member.text}
                        color="colorful"
                        size={20}
                        className={styles.chipAvatar}
                      />
                      <Text size={200}>{member.text}</Text>
                    </span>
                  ))}
                </div>
              </Field>
            )}

            <Field
              label={
                isAddOnly
                  ? DIALOG_LABELS.ADD_NEW_MEMBERS
                  : DIALOG_LABELS.TEAM_MEMBERS
              }
            >
              <PeoplePicker
                id={PICKER_IDS.TEAM_MEMBERS}
                handleSelectedItems={
                  isAddOnly ? handleNewMembers : handleSelectedTeamMembers
                }
                disabled={isSaving}
                required={true}
                hasOnlyOurUsers={true}
                readResourcesForPickerFieldFiltered={
                  readResourcesForPickerFieldFiltered
                }
                selectedOptions={isAddOnly ? newMembers : teamMembers}
              />
            </Field>

            {errorMessage && (
              <div className={footerStyles.dialogErrorMessage} role="alert">
                {errorMessage}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={() => props.onDismiss(false)}
              disabled={isSaving}
            >
              {DIALOG_LABELS.CANCEL}
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {DIALOG_LABELS.SAVE}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
